// Surgical / OBG / Paediatric add-on records — docs/HMS_V4_IP_CASE_SHEET_
// INDIA_DESIGN.md §8, Phase 8. "Opts in per admission": nothing is attached
// until a clinician picks a template and starts filling it in (PUT creates
// the row), matching the printed packet's own "prints only when attached"
// rule.
//
// List-field capture (implants, transfusion, previous_deliveries,
// immunisation) is deliberately NOT built here this phase — see
// specialtyRecordFields.ts's own header comment.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useSpecialtyRecords, useSaveSpecialtyRecord, useSignSpecialtyRecord } from '../hooks';
import { SpecialtyRecordForm } from '../SpecialtyRecordForm';
import { SPECIALTY_GROUPS } from '../specialtyRecordFields';
import type { SpecialtyRecord, SpecialtyTemplate } from '../types';
import { Section, Empty, useCan, fmtDateTime } from '../ui';

const TEMPLATE_META: { code: SpecialtyTemplate; label: string }[] = [
  { code: 'surgical', label: 'Surgical' },
  { code: 'obg', label: 'OBG' },
  { code: 'paediatric', label: 'Paediatric' },
];

function StatusBadge({ record }: { record: SpecialtyRecord | undefined }) {
  if (!record) return <Badge variant="neutral">Not attached</Badge>;
  if (record.status === 'signed') return <Badge variant="success">Signed</Badge>;
  return <Badge variant="warn">Draft</Badge>;
}

export function SpecialtyRecordsSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mayWrite = can('ip:specialty-record') && !readOnly;

  const { data: records, isLoading } = useSpecialtyRecords(admissionId);
  const save = useSaveSpecialtyRecord(admissionId);
  const sign = useSignSpecialtyRecord(admissionId);

  const [active, setActive] = useState<SpecialtyTemplate | null>(null);
  const [draftData, setDraftData] = useState<Record<string, unknown>>({});
  const [err, setErr] = useState<string | null>(null);

  const activeRecord = records?.find((r) => r.template === active);

  useEffect(() => {
    setDraftData(activeRecord?.data || {});
    setErr(null);
  }, [active, activeRecord?.id, activeRecord?.data]);

  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');

  const isSigned = activeRecord?.status === 'signed';

  return (
    <Section title="Specialty add-ons" subtitle="Surgical, OBG and Paediatric pages — only attached templates print (§8).">
      {isLoading ? <LoadingState /> : (
        <>
          <div className="mb-3 flex flex-wrap gap-2">
            {TEMPLATE_META.map((t) => {
              const rec = records?.find((r) => r.template === t.code);
              return (
                <button
                  key={t.code}
                  type="button"
                  onClick={() => setActive(t.code)}
                  className={`flex items-center gap-2 rounded-[var(--radius)] border px-3 py-1.5 text-[13px] ${active === t.code ? 'border-teal bg-teal/5 font-medium' : 'border-border'}`}
                >
                  {t.label}
                  <StatusBadge record={rec} />
                </button>
              );
            })}
          </div>

          {!active && <Empty label="Pick a template above to view or attach it." />}

          {active && (
            <div>
              {err && <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}

              {isSigned && (
                <div className="mb-3 text-[12px] text-ink-muted">
                  Signed by {activeRecord?.signed_by} &middot; {fmtDateTime(activeRecord?.signed_at)}
                </div>
              )}
              {!mayWrite && !isSigned && (
                <div className="mb-3 text-[12px] text-ink-muted">You don't have permission to edit this record.</div>
              )}

              <SpecialtyRecordForm
                groups={SPECIALTY_GROUPS[active]}
                data={draftData}
                onChange={setDraftData}
                readOnly={!mayWrite || isSigned}
              />

              {mayWrite && !isSigned && (
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => {
                      setErr(null);
                      save.mutate({ template: active, data: draftData }, { onError: fail });
                    }}
                  >
                    {save.isPending ? 'Saving…' : 'Save draft'}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    disabled={!activeRecord || sign.isPending}
                    onClick={() => {
                      if (!activeRecord) return;
                      setErr(null);
                      sign.mutate(activeRecord.id, { onError: fail });
                    }}
                  >
                    {sign.isPending ? 'Signing…' : 'Sign'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
